import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { AgentView, RunSummary } from '@app/shared';
import { TERMINAL_RUN_STATUSES } from '@app/shared';
import {
  Button,
  Cluster,
  CodeBlock,
  ConfirmDialog,
  type Column,
  DataTable,
  EmptyState,
  FilterBar,
  FilterField,
  Icon,
  Icons,
  IconButton,
  PageHeader,
  SelectMenu,
  StatusBadge,
  Stack,
  Switch,
  Text,
  TextInput,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useScopedWorkspace } from '../lib/operatorWorkspace';
import { OperatorWorkspaceSelect } from '../components/OperatorWorkspaceSelect';
import { useOperationsStream } from '../lib/useOperationsStream';
import { durationMs, formatDuration } from '../lib/duration';
import { formatCost } from '../lib/formatCost';
import { AGENT_KIND_LABELS } from './agentBuilderDefaults';
import { RunOutcomeBadge } from '../components/RunOutcomeBadge';

/** Runs-list outcome filter options (§14, #263) — segmented-style choices in a select. */
type OutcomeFilter = 'results' | 'no_action' | 'failed' | 'all';
const OUTCOME_OPTIONS: { value: OutcomeFilter; label: string }[] = [
  { value: 'results', label: 'Results' },
  { value: 'no_action', label: 'No action' },
  { value: 'failed', label: 'Failed' },
  { value: 'all', label: 'All' },
];

const runsRoute = getRouteApi('/app/runs');

const TERMINAL: readonly string[] = TERMINAL_RUN_STATUSES;

/** A run may be deleted only once it is terminal (has no active continuation job). */
function isDeletable(status: string): boolean {
  return TERMINAL.includes(status);
}

/** Start of the local day `days` ago, as an ISO instant — the lower bound of a window. */
function startOfDaysAgo(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/** A `type="date"` value (YYYY-MM-DD) → the ISO instant at the START of that local day. */
function dateInputToAfterIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** A `type="date"` value → the ISO instant at the END of that local day (inclusive To). */
function dateInputToBeforeIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** An ISO instant → the `type="date"` value (YYYY-MM-DD) of its local day. */
function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The per-run elapsed label (#114): final duration if ended, live-so-far if started, else "—". */
function runDurationLabel(run: RunSummary): string {
  const startedIso = run.startedAt ?? run.createdAt;
  if (run.endedAt != null) return formatDuration(durationMs(startedIso, run.endedAt)) ?? '—';
  if (run.startedAt != null)
    return formatDuration(durationMs(run.startedAt, new Date().toISOString())) ?? '—';
  return '—';
}

/**
 * A run's start time as a compact, readable local string (#307) — the primary Timing
 * value ("Jul 15, 2026, 12:00 AM"). Drops the noisy seconds `toLocaleString` emits so
 * the moment a run ran reads clearly at a glance; the full timestamp rides the `title`.
 */
function formatRunStart(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The Runs experience (§14 operations). The runs DataTable is a plain full-width master
 * list; clicking a row navigates DIRECTLY to the full run detail page
 * (`/app/runs/$runId/full`, #297) — the earlier split-pane preview (#279) is removed, and
 * the full page's collapsible large prompt (#283) covers what the preview addressed.
 * The runs filter search rides the hop (`search: (prev) => prev`) so the full page's
 * "Back to runs" returns the list exactly as the operator left it.
 *
 * Live via SSE — the stream invalidates the runs query on any run update. URL-persisted
 * filters (agent #112, date window #130, includeArchived #215) survive refresh and are
 * shareable, and compose. Terminal runs can be deleted (confirm → DELETE → refetch).
 */
export function RunsListPage() {
  const queryClient = useQueryClient();

  const { agentId, threadId, after, before, includeArchived, outcome, ws } = runsRoute.useSearch();
  const navigate = runsRoute.useNavigate();
  // #278: an operator can VIEW another workspace's runs by selecting it in the dropdown;
  // the choice rides the `ws` search param so it survives list → full detail → Back. A
  // non-operator has no dropdown and `ws` is ignored (`workspaceId` = their own).
  const { workspaceId, ownWorkspaceId, viewOnly } = useScopedWorkspace(ws);
  // SSE is scoped to the caller's OWN workspace; a foreign (view-only) workspace falls back
  // to non-live (the stream ticket is member-gated) rather than crash the operator view.
  useOperationsStream(viewOnly ? null : workspaceId);
  // #263: default view is result-producing runs (+ active); URL omits the default.
  const outcomeFilter: OutcomeFilter = outcome ?? 'results';
  const showArchived = includeArchived ?? false;

  const [toDelete, setToDelete] = useState<RunSummary | null>(null);

  const agents = useQuery({
    queryKey: [...qk.agents(workspaceId), { includeArchived: showArchived }] as const,
    queryFn: () =>
      api.listAgents(workspaceId, { pageSize: 100, includeArchived: showArchived || undefined }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const agentsById = useMemo(() => {
    const map = new Map<string, AgentView>();
    for (const a of agents.data?.data ?? []) map.set(a.id, a);
    return map;
  }, [agents.data]);

  const runs = useQuery({
    queryKey: qk.runsList(
      workspaceId,
      agentId ?? null,
      after ?? null,
      before ?? null,
      showArchived,
      outcomeFilter,
      threadId ?? null,
    ),
    queryFn: () =>
      api.listRuns(workspaceId, {
        pageSize: 100,
        agentId,
        threadId,
        createdAfter: after,
        createdBefore: before,
        includeArchived: showArchived || undefined,
        outcome: outcomeFilter,
      }),
  });

  const del = useMutation({
    mutationFn: (runId: string) => api.deleteRun(workspaceId, runId),
    onSuccess: async () => {
      setToDelete(null);
      await queryClient.invalidateQueries({ queryKey: qk.runs(workspaceId) });
    },
  });

  const setRange = (nextAfter?: string, nextBefore?: string): void => {
    void navigate({ search: (prev) => ({ ...prev, after: nextAfter, before: nextBefore }) });
  };
  const onAgentChange = (value: string): void =>
    void navigate({ search: (prev) => ({ ...prev, agentId: value || undefined }) });
  const onIncludeArchivedChange = (checked: boolean): void =>
    void navigate({ search: (prev) => ({ ...prev, includeArchived: checked || undefined }) });
  // #263: switch outcome bucket; 'results' is the default, so omit it from the URL.
  const onOutcomeChange = (value: string): void =>
    void navigate({
      search: (prev) => ({
        ...prev,
        outcome: value === 'results' ? undefined : (value as OutcomeFilter),
      }),
    });
  // #278: switch the workspace being viewed; the own workspace omits `ws` from the URL.
  const onWorkspaceChange = (id: string): void =>
    void navigate({ search: (prev) => ({ ...prev, ws: id === ownWorkspaceId ? undefined : id }) });
  // #299: clear the thread-correlation filter, keeping every other filter intact.
  const clearThread = (): void =>
    void navigate({ search: (prev) => ({ ...prev, threadId: undefined }) });

  // #297: a row click navigates DIRECTLY to the full run detail page, carrying the runs
  // filter search so the full page's "Back to runs" restores the list as the operator left it.
  const openRun = (run: RunSummary): void =>
    void navigate({
      to: '/app/runs/$runId/full',
      params: { runId: run.id },
      search: (prev) => prev,
    });

  const agentOptions = useMemo(
    () => [
      { value: '', label: 'All agents' },
      ...(agents.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    ],
    [agents.data],
  );

  const columns: Column<RunSummary>[] = [
    {
      id: 'status',
      header: 'Outcome',
      width: '10rem',
      hideCardLabel: true,
      // #263: the derived-outcome badge (result names the kind; no_result → muted "No
      // action"; failed/active keep the raw status chip).
      cell: (run) => <RunOutcomeBadge run={run} size="sm" />,
    },
    {
      id: 'run',
      header: 'Run',
      hideCardLabel: true,
      // #314: the fluid run column takes the leftover width and TRUNCATES (the other
      // columns are fixed-width). `maxWidth: '0'` is the table-cell flex-truncate trick —
      // the DataTable clips overflow on the cell so the inner `truncate` texts ellipsize,
      // stopping the long outcome/reason from widening the table off-screen.
      maxWidth: '0',
      cell: (run) => {
        const agent = run.agentId ? agentsById.get(run.agentId) : undefined;
        const outcome = run.endedReason ?? run.progressSummary ?? undefined;
        return (
          // #304: the full run id lives on the cell `title` (tooltip) for reference — the
          // row navigates to the full detail on click ("click to see more"), so the visible
          // "sha" chip is dropped and the secondary line always shows the outcome/reason.
          <Stack gap="1" title={`Run ${run.id}`}>
            <Cluster gap="2" wrap>
              <Text weight="semibold" truncate data-testid="run-row-agent-name">
                {agent?.name ?? 'Unknown agent'}
              </Text>
              {agent && (
                <StatusBadge
                  tone="neutral"
                  size="sm"
                  label={AGENT_KIND_LABELS[agent.kind]}
                  data-testid="run-agent-kind"
                />
              )}
              {run.label && (
                <StatusBadge tone="info" size="sm" label={run.label} data-testid="run-label" />
              )}
            </Cluster>
            {/* #304: always the outcome/reason text — prefer the OUTCOME (endedReason) for a
                finished run, then live progress, then a graceful fallback. Full-width, truncated.
                #314: the full outcome rides the `title` so hovering the truncated line reveals it. */}
            <Text size="sm" tone="secondary" truncate data-testid="run-summary" title={outcome}>
              {outcome ?? 'No summary yet'}
            </Text>
          </Stack>
        );
      },
    },
    {
      id: 'cost',
      header: 'Cost',
      align: 'end',
      width: '6rem',
      cardLabel: 'Cost',
      cell: (run) => (
        <Text as="span" size="sm" mono data-testid="run-cost" title="Total cost for this run">
          {formatCost(typeof run.totals.costUsd === 'number' ? run.totals.costUsd : 0)}
        </Text>
      ),
    },
    {
      id: 'timing',
      header: 'Timing',
      align: 'end',
      width: '15rem',
      cardLabel: 'Timing',
      cell: (run) => {
        const startedIso = run.startedAt ?? run.createdAt;
        return (
          // #307: the START time is the prominent, readable primary value (with a clock
          // glyph so it reads as "when it ran"); the duration is a clear labelled line
          // beneath it. The full timestamp lives on the primary line's `title` tooltip.
          <Stack gap="1" align="end">
            <Cluster gap="2" align="center" wrap={false}>
              <Icon as={Icons.Clock} size="xs" tone="muted" />
              <Text
                as="span"
                size="sm"
                weight="semibold"
                data-testid="run-started"
                title={`Started ${new Date(startedIso).toLocaleString()}`}
              >
                {formatRunStart(startedIso)}
              </Text>
            </Cluster>
            <Cluster gap="2" align="center" wrap={false}>
              <Text as="span" size="xs" tone="secondary">
                Ran for
              </Text>
              <Text as="span" size="sm" weight="medium" data-testid="run-duration">
                {runDurationLabel(run)}
              </Text>
            </Cluster>
            {run.endedAt && (
              <Text as="span" size="2xs" tone="muted" data-testid="run-ended">
                Ended {new Date(run.endedAt).toLocaleString()}
              </Text>
            )}
          </Stack>
        );
      },
    },
  ];

  const emptyState = (
    <EmptyState
      icon={Icons.History}
      title="No runs yet"
      description="Runs appear here when an agent is triggered — publish an agent, then send it an email or run it manually."
      action={
        <Button
          onClick={() => void navigate({ to: '/app/agents' })}
          data-testid="empty-go-to-agents"
        >
          Go to Agents
        </Button>
      }
    />
  );

  const list = (
    <Stack gap="4">
      {del.isError && (
        <Text tone="danger" size="sm" data-testid="runs-delete-error">
          {del.error instanceof ApiError ? del.error.message : 'Delete failed.'}
        </Text>
      )}
      <DataTable
        caption="Runs in this workspace"
        columns={columns}
        rows={runs.data?.data ?? []}
        getRowId={(r) => r.id}
        onRowClick={openRun}
        rowTestId="run-row"
        loading={runs.isLoading}
        error={runs.isError}
        onRetry={() => void runs.refetch()}
        isEmpty={(runs.data?.data.length ?? 0) === 0}
        emptyState={emptyState}
        // Delete is owner/member-gated — hidden entirely when viewing a foreign workspace.
        rowActions={
          viewOnly
            ? undefined
            : (run) => {
                const deletable = isDeletable(run.status);
                return (
                  <IconButton
                    aria-label="Delete run"
                    variant="ghost"
                    size="sm"
                    disabled={!deletable || del.isPending}
                    title={
                      deletable
                        ? 'Delete run'
                        : 'Only terminal runs can be deleted — cancel it first'
                    }
                    onClick={() => setToDelete(run)}
                    data-testid="run-delete"
                    icon={
                      <Icon as={Icons.Trash2} size="sm" tone={deletable ? 'danger' : 'muted'} />
                    }
                  />
                );
              }
        }
      />
    </Stack>
  );

  return (
    <Stack gap="6">
      <PageHeader
        title="Runs"
        description="Every agent run in this workspace — live."
        actions={
          /* #278: operator-only workspace dropdown (self-hides for a non-operator). */
          <OperatorWorkspaceSelect
            value={workspaceId}
            ownWorkspaceId={ownWorkspaceId}
            onChange={onWorkspaceChange}
          />
        }
      />
      <FilterBar
        actions={
          <Switch
            checked={showArchived}
            onChange={(e) => onIncludeArchivedChange(e.currentTarget.checked)}
            label="Include archived agents' runs"
            data-testid="runs-include-archived"
          />
        }
      >
        <FilterField label="Outcome">
          {() => (
            <SelectMenu
              value={outcomeFilter}
              onChange={onOutcomeChange}
              options={OUTCOME_OPTIONS}
              label="Filter runs by outcome"
              size="sm"
              minWidth="10rem"
              data-testid="runs-outcome-filter"
            />
          )}
        </FilterField>
        <FilterField label="Agent">
          {() => (
            <SelectMenu
              value={agentId ?? ''}
              onChange={onAgentChange}
              options={agentOptions}
              label="Filter runs by agent"
              placeholder="All agents"
              size="sm"
              minWidth="12rem"
              data-testid="runs-agent-filter"
            />
          )}
        </FilterField>
        <FilterField label="From">
          {({ id }) => (
            <span data-testid="runs-date-from">
              <TextInput
                id={id}
                type="date"
                size="sm"
                value={isoToDateInput(after)}
                onChange={(e) => setRange(dateInputToAfterIso(e.target.value), before)}
              />
            </span>
          )}
        </FilterField>
        <FilterField label="To">
          {({ id }) => (
            <span data-testid="runs-date-to">
              <TextInput
                id={id}
                type="date"
                size="sm"
                value={isoToDateInput(before)}
                onChange={(e) => setRange(after, dateInputToBeforeIso(e.target.value))}
              />
            </span>
          )}
        </FilterField>
        <FilterField label="Quick range" hideLabel>
          {() => (
            <Cluster gap="2" wrap>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRange(startOfDaysAgo(0), undefined)}
                data-testid="runs-range-today"
              >
                Today
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRange(startOfDaysAgo(7), undefined)}
                data-testid="runs-range-7d"
              >
                Last 7 days
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRange(startOfDaysAgo(30), undefined)}
                data-testid="runs-range-30d"
              >
                Last 30 days
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRange(undefined, undefined)}
                data-testid="runs-range-all"
              >
                All
              </Button>
            </Cluster>
          )}
        </FilterField>
      </FilterBar>

      {/* #299: active thread-correlation filter — a small chip with a clear, shown only
          when the list is narrowed to one email thread (from a run-detail thread link). */}
      {threadId && (
        <Cluster gap="2" align="center" data-testid="runs-thread-filter">
          <StatusBadge
            tone="info"
            size="sm"
            label={`Thread: ${threadId.slice(0, 12)}${threadId.length > 12 ? '…' : ''}`}
          />
          <Button
            variant="ghost"
            size="sm"
            iconStart={<Icon as={Icons.X} size="sm" />}
            onClick={clearThread}
            data-testid="runs-thread-clear"
          >
            Clear
          </Button>
        </Cluster>
      )}

      {list}

      <ConfirmDialog
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && del.mutate(toDelete.id)}
        title="Delete run?"
        tone="danger"
        confirmLabel="Delete"
        loading={del.isPending}
        confirmTestId="run-delete-confirm"
        description={
          <Text tone="secondary" size="md">
            This permanently deletes run <CodeBlock inline>{toDelete?.id.slice(0, 8)}</CodeBlock>{' '}
            and its transcript, approvals, usage records, and outputs. This cannot be undone.
          </Text>
        }
      />
    </Stack>
  );
}
